import { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import {
  joinArticle,
  sectionsFor,
  splitArticle,
  switchFormat,
  type ArticleFormat,
  type ArticleParts,
} from '@esa/shared';
import { Button, Field, Input, Segmented, Textarea } from '../ui/index.js';

type TextKey = Exclude<keyof ArticleParts, 'steps' | 'other' | 'format'>;

/** The section an author must fill for each format: what it is about. */
const FIRST: Record<ArticleFormat, TextKey> = { troubleshooting: 'symptom', information: 'question' };

/**
 * An article's body as its house sections, instead of one box the author has
 * to fill with headings by hand. Takes and returns the stored body, so the
 * callers still save one string.
 *
 * Two formats (shared/articleSections.ts): Troubleshooting — Symptom, Applies
 * to, Steps, Check it worked, If that did not work — and Information —
 * Question, Applies to, Answer, More information — for what has no steps:
 * a policy, where something is, the status of something. The switch moves
 * what it can to its counterpart and keeps the rest under "Anything else".
 *
 * Anything outside the sections (older articles, notes for agents) shows
 * under "Anything else" and is saved as written.
 */
export function ArticleSectionsEditor({
  value,
  onChange,
  required = false,
}: {
  value: string;
  onChange: (body: string) => void;
  /** Mark the parts a new article needs: Symptom and Steps, or Question and Answer. */
  required?: boolean;
}) {
  const [parts, setParts] = useState<ArticleParts>(() => withAStep(splitArticle(value)));
  // Re-split only when the body changes from OUTSIDE (a new article loaded,
  // AI review applied) — not on every keystroke here, which would reformat
  // what is being typed.
  const lastOut = useRef(value);
  useEffect(() => {
    if (value !== lastOut.current) {
      setParts(withAStep(splitArticle(value)));
      lastOut.current = value;
    }
  }, [value]);

  const update = (next: ArticleParts) => {
    setParts(next);
    const body = joinArticle(next);
    lastOut.current = body;
    onChange(body);
  };
  const setText = (key: TextKey | 'other', text: string) => update({ ...parts, [key]: text });
  const setStep = (i: number, text: string) => update({ ...parts, steps: parts.steps.map((s, j) => (j === i ? text : s)) });
  const moveStep = (i: number, d: number) => {
    const steps = [...parts.steps];
    const [s] = steps.splice(i, 1);
    steps.splice(i + d, 0, s!);
    update({ ...parts, steps });
  };
  const removeStep = (i: number) => update({ ...parts, steps: withAStep({ ...parts, steps: parts.steps.filter((_, j) => j !== i) }).steps });
  const setFormat = (to: ArticleFormat) => {
    const next = withAStep(switchFormat(parts, to));
    if (next.other.trim()) setShowOther(true);
    update(next);
  };

  const [showOther, setShowOther] = useState(parts.other.trim() !== '');
  const isInfo = parts.format === 'information';

  return (
    <div className="space-y-3">
      <Field
        label="Kind of article"
        hint={
          isInfo
            ? 'A question and its answer: a policy, where to find something, or how something stands. No steps.'
            : 'A problem and the actions that fix it, in order.'
        }
      >
        <Segmented
          value={parts.format}
          onChange={setFormat}
          size="sm"
          className="max-w-xs"
          aria-label="Kind of article"
          options={[
            { value: 'troubleshooting', label: 'Troubleshooting' },
            { value: 'information', label: 'Information' },
          ]}
        />
      </Field>

      {sectionsFor(parts.format).map((s) =>
        s.key === 'steps' ? (
          <Field key={s.key} label={s.heading} hint={s.hint} required={required}>
            <ol className="space-y-1.5">
              {parts.steps.map((step, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="nums mt-1.5 w-5 shrink-0 text-right text-2xs text-ink-tertiary">{i + 1}.</span>
                  <Input
                    value={step}
                    onChange={(e) => setStep(i, e.target.value)}
                    placeholder={i === 0 ? 'Open the timesheet in Vantagepoint' : 'Next action'}
                    aria-label={`Step ${i + 1}`}
                  />
                  <Button variant="ghost" size="sm" iconOnly aria-label="Move up" disabled={i === 0} onClick={() => moveStep(i, -1)}>
                    <ArrowUp aria-hidden className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label="Move down"
                    disabled={i === parts.steps.length - 1}
                    onClick={() => moveStep(i, 1)}
                  >
                    <ArrowDown aria-hidden className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" iconOnly aria-label={`Remove step ${i + 1}`} onClick={() => removeStep(i)}>
                    <X aria-hidden className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ol>
            <Button
              variant="ghost"
              size="sm"
              className="mt-1"
              leading={<Plus />}
              onClick={() => update({ ...parts, steps: [...parts.steps, ''] })}
            >
              Add a step
            </Button>
          </Field>
        ) : (
          <Field
            key={s.key}
            label={s.heading}
            hint={s.hint}
            required={required && (s.key === FIRST[parts.format] || s.key === 'answer')}
          >
            <Textarea
              rows={s.key === 'answer' ? 4 : 2}
              value={parts[s.key]}
              onChange={(e) => setText(s.key, e.target.value)}
              placeholder={s.key === 'question' ? 'Where can I find the employee handbook?' : undefined}
              aria-label={s.heading}
            />
          </Field>
        ),
      )}

      {showOther ? (
        <Field label="Anything else" hint="Text outside the sections above. Kept as written, after them.">
          <Textarea rows={4} value={parts.other} onChange={(e) => setText('other', e.target.value)} aria-label="Anything else" />
        </Field>
      ) : (
        <button type="button" className="text-2xs text-ink-tertiary hover:text-ink-secondary" onClick={() => setShowOther(true)}>
          + Anything else
        </button>
      )}
    </div>
  );
}

/** Always one step box to type into — in a troubleshooting article. */
function withAStep(p: ArticleParts): ArticleParts {
  return p.format === 'troubleshooting' && p.steps.length === 0 ? { ...p, steps: [''] } : p;
}

/** The same sections, read-only: headings as headings and steps as a list, not raw markdown. */
export function ArticleSectionsView({ body }: { body: string }) {
  const p = splitArticle(body);
  const sections = sectionsFor(p.format);
  const any = sections.some((s) => (s.key === 'steps' ? p.steps.length > 0 : p[s.key].trim() !== ''));
  if (!any) {
    return <p className="whitespace-pre-wrap text-xs leading-relaxed text-ink">{body}</p>;
  }
  return (
    <div className="space-y-3 text-xs leading-relaxed text-ink">
      {sections.map((s) => {
        if (s.key === 'steps') {
          return p.steps.length ? (
            <section key={s.key}>
              <h4 className="text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">{s.heading}</h4>
              <ol className="mt-1 list-decimal space-y-0.5 pl-5">
                {p.steps.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ol>
            </section>
          ) : null;
        }
        const text = p[s.key].trim();
        return text ? (
          <section key={s.key}>
            <h4 className="text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">{s.heading}</h4>
            <p className="mt-1 whitespace-pre-wrap">{text}</p>
          </section>
        ) : null;
      })}
      {p.other.trim() && <p className="whitespace-pre-wrap border-t border-line-subtle pt-2 text-ink-secondary">{p.other}</p>}
    </div>
  );
}
